import type { CSSProperties } from 'react'
import PickCell from './PickCell'
import type { DraftState, PlayerSummary } from '../../types/protocol'

// Team columns are fixed in draft order and never reorder: each cell is that
// team's pick for that round, which the pre-generated pick grid guarantees is
// 1:1. Snake order is conveyed by the #overall on each card — reordering
// columns every round would make the board unreadable from a couch, because
// the one thing a viewer tracks is "which column is mine".

interface Props {
  draft: DraftState
  playersById: Map<number, PlayerSummary>
}

export default function PickGrid({ draft, playersById }: Props) {
  const teams = draft.teams
  const rounds = Math.max(1, draft.rounds)

  if (teams.length === 0) {
    return <p className="text-board-hero font-display p-[4vh] text-ink-lo">No teams yet.</p>
  }

  // Round + team is unique across the grid, so one flat map beats scanning
  // draft.picks once per cell (14 x 16 x 224 comparisons otherwise).
  const pickMap = new Map(draft.picks.map((pick) => [`${pick.round}-${pick.team_id}`, pick]))

  // Consumed by .board-grid's grid-template-* in application.css.
  const gridVars = { '--teams': teams.length, '--rounds': rounds } as unknown as CSSProperties

  return (
    <div
      className="board-grid min-h-0 flex-1 gap-[0.25vh] p-[0.35vh]"
      style={gridVars}
    >
      {/* Header row: empty gutter corner, then the team names. */}
      <div />
      {teams.map((team) => (
        <div
          key={team.id}
          className="flex min-w-0 flex-col justify-center overflow-hidden rounded-[0.5vh] bg-surface-2 px-[0.35vw] py-[1.5vh] text-center shadow-raised"
        >
          <p className="text-board-team font-display truncate leading-[1.1] font-bold text-ink-hi">
            {team.name}
          </p>
          <p className="text-board-sub truncate leading-[1.2] font-medium text-ink-lo">
            {team.owner_name ?? 'Unclaimed'}
          </p>
        </div>
      ))}

      {Array.from({ length: rounds }, (_, index) => index + 1).map((round) => (
        <RoundRow
          key={round}
          round={round}
          teams={teams}
          pickMap={pickMap}
          playersById={playersById}
          currentOverall={draft.current_pick_overall}
        />
      ))}
    </div>
  )
}

function RoundRow({
  round,
  teams,
  pickMap,
  playersById,
  currentOverall,
}: {
  round: number
  teams: DraftState['teams']
  pickMap: Map<string, DraftState['picks'][number]>
  playersById: Map<number, PlayerSummary>
  currentOverall: number | null
}) {
  return (
    <>
      <div className="flex items-center justify-center">
        <span className="text-board-round font-display font-bold text-ink-lo tabular-nums">
          {round}
        </span>
      </div>
      {teams.map((team) => {
        const pick = pickMap.get(`${round}-${team.id}`)
        const player = pick?.player_id != null ? (playersById.get(pick.player_id) ?? null) : null
        return (
          <PickCell
            key={team.id}
            pick={pick}
            player={player}
            isCurrent={pick != null && pick.overall === currentOverall}
          />
        )
      })}
    </>
  )
}
